<template>
  <div>
    <editRolesModal ref="editRolesModal" :roles="roles" @editRoles="editRoles"/>
    <editDealsModal ref="editDealsModal" :deals="deals" @editDeals="editDeals"/>
    <editScenariosModal ref="editScenariosModal" @editScenarios="editScenarios"/>
    <table class="table table-sm color mb-2">
      <caption class="caption table-caption">Users</caption>
      <thead>
      <tr>
        <!-- UserName -->
        <th class="text-left text-nowrap">Username</th>
        <!-- Roles -->
        <th class="text-left text-nowrap">Roles</th>
        <!-- Edit Roles -->
        <th></th>
        <!-- Edit Deals -->
        <th></th>
        <!-- Edit Scenarios -->
        <!-- <th></th> -->
        <!-- Reset Password -->
        <th></th>
        <!-- Disable User -->
        <th></th>
        <!-- Delete User -->
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="user in users" :key=user.Id v-bind:class="[{ disabled: user.IsEnabled == false }]">
        <!-- UserName -->
        <td class="text-left align-middle">{{ user.Username }}</td>
        <!-- Roles -->
        <td class="text-left align-middle">
          <div v-for="role in user.Roles" :key=role>{{ role }}</div>
        </td>
        <!-- Edit Roles -->
        <td class="align-middle">
          <b-btn variant="link" @click="showEditRolesModal(user)" v-bind:disabled="!user.IsEnabled">Edit roles</b-btn>
        </td>
        <!-- Edit Deals -->
        <td class="align-middle">
          <b-btn variant="link" @click="showEditDealsModal(user)" v-bind:disabled="!user.IsEnabled">Edit deals</b-btn>
        </td>
        <!-- Edit Scenarios -->
        <!-- <td class="align-middle"><b-btn variant="link" @click="showEditScenariosModal(user)" v-bind:disabled="!user.IsEnabled">Edit scenarios</b-btn></td> -->
        <!-- Reset Password -->
        <td class="align-middle">
          <b-btn variant="link" @click="resetPassword(user)" v-bind:disabled="!user.IsEnabled">Reset password</b-btn>
        </td>
        <!-- Disable User -->
        <td class="align-middle">
          <b-btn variant="link" @click="updateUser(user, !user.IsEnabled)">
            {{ user.IsEnabled ? 'Disable user' : 'Enable user' }}
          </b-btn>
        </td>
        <!-- Delete User -->
        <td class="align-middle">
          <b-btn variant="link" @click="deleteUser(user)">Delete user</b-btn>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import service            from '../../services/HttpRequestService';
import EditRolesModal     from './EditRolesModal';
import EditDealsModal     from './EditDealsModal';
import EditScenariosModal from './EditScenariosModal';

import { UserNotifications, withUINotification } from '../../hooks/UserNotifications';

export default {
  props: ['users', 'roles', 'deals', 'scenarios'],
  async created() {
    this.withUINotification = withUINotification;
  },
  methods: {
    showEditRolesModal(user) {
      this.$refs.editRolesModal.showModal(user, user.Roles);
    },
    async showEditDealsModal(user) {
      await this.withUINotification(async () => {
        let { data: deals = [] } = await service.getAllDeals(user.Id);
        deals = deals.map((el) => {
          el.IsAssignedOriginal = el.IsAssigned;
          return el;
        });
        this.$refs.editDealsModal.showModal(user, deals);
      },
      'Failed to load user\'s available deals.');
    },
    async showEditScenariosModal(user) {
      await this.withUINotification(async () => {
        let { data: scenarios = [] } = await service.getAllScenarios(user.Id);
        scenarios = scenarios.map((el) => {
          el.IsAssignedOriginal = el.IsAssigned;
          return el;
        });
        this.$refs.editScenariosModal.showModal(user, scenarios)
      },
      'Failed to load user\'s scenarios.');
    },
    async editRoles(user, roles = []) {
      await this.withUINotification(async () => {
        await service.editRoles(user.Id, roles)
        UserNotifications.UserChanged(user.Id).emit(this)
      },
      'Failed to modify user\'s roles.',
      'User\'s roles have been updated.');
    },
    async editDeals(user, deals = []) {
      await this.withUINotification(async () => {
        const userDeals = deals.filter(el => el.IsAssigned !== el.IsAssignedOriginal);
        await service.assignDealsToUser(userDeals, user.Id);
        UserNotifications.UserChanged(user.Id).emit(this)
      },
      'Failed to modify user\'s deals list.',
      'User\'s deals list have been updated.');
    },
    async editScenarios(user, scenarios = []) {
      await this.withUINotification(async () => {
        const userScenarios = scenarios.filter(el => el.IsAssigned !== el.IsAssignedOriginal);
        await service.assignScenariosToUser(userScenarios, user.Id);
        UserNotifications.UserChanged(user.Id).emit(this)
      },
      'Failed to modify user\'s scenarios list.',
      'User\'s scenarios list have been updated.')
    },
    async resetPassword(user) {
      const strQuestion    = `Do you want to reset ${user.Username || user.Id}'s password?`
      const resetConfirmed = await this.$root.confirmCurrentStep(strQuestion)
      if (!resetConfirmed) return;
      await this.withUINotification(async () => {
        const { status = 504 } = await service.resetPassword(user.Id)
        if (status === 204) {
          throw new Error(`Make sure that a user has a confirmed email address.`)
        }
      },
      'Failed to initiate user\'s password reset.',
      'User\'s password reset has been initiated.');
    },
    async updateUser(user, isEnabled) {
      const strQuestion      = `Do you want to ${isEnabled ? 'enable' : 'disable'} user ${user.Username}`;
      const disableConfirmed = await this.$root.confirmCurrentStep(strQuestion);
      if (!disableConfirmed) return;
      await this.withUINotification(async () => {
        await service.updateUser(user.Id, isEnabled);
        UserNotifications.UserChanged(user.Id).emit(this)
      },
      'Failed to update user\'s info.',
      'User\'s info has been updated.');
    },
    async deleteUser(user) {
      const strQuestion     = 'Do you want to delete user ' + user.Username;
      const deleteConfirmed = await this.$root.confirmCurrentStep(strQuestion);
      if (!deleteConfirmed) return;
      await this.withUINotification(async () => {
        await service.deleteUser(user.Id);
      },
      'Failed to delete the user.',
      `The user ${user.Username} has been deleted.`);
    }
  },
  components: {
    EditRolesModal,
    EditDealsModal,
    EditScenariosModal
  }
};
</script>

<style lang="scss" scoped>
.disabled {
  background-color: gainsboro;
}
</style>
